import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { FormikField } from "@air/formik-field";
import { Button } from "@air/primitive-button";
import { Input } from "@air/primitive-input";
import { Modal, ModalCloseButton, ModalTitle } from "@air/primitive-modal";
import { useToasts } from "@air/provider-toast";
import { Form, Formik } from "formik";
import React, { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import * as Yup from "yup";
import { PrivateBoardSearch } from "~/components/BoardSearch/PrivateBoardSearch";
import { useSingleBoardSearchState } from "~/components/BoardSearch/shared/hooks";
import { ToastLink } from "~/components/UI/ToastLink";
import { CREATED_BOARD_LINK } from "~/constants/testIDs";
import { useBoardPermissionsCache } from "~/hooks/useBoardPermissionsCache";
import { useGoToBoardPage } from "~/hooks/useGoToBoardPage";
import { useLibraryPermissionsCache } from "~/hooks/useLibraryPermissionsCache";
import { useShowSubscriptionExpiredModal } from "~/hooks/useShowSubscriptionExpiredModal";
import { centralizedBoardSelector } from "~/store/centralizedBoard/selectors";
import { selectedClipIdsSelector } from "~/store/selectedItems/selectors";
import { useCreateNewBoard } from "~/swr-hooks/boards/useCreateNewBoard";
import { containsRestrictedPathChars } from "~/utils/FileUtils";
import { canCreateBoard } from "~/utils/permissions/boardPermissions";
const CreateBoardSchema = Yup.object({
    title: Yup.string().trim().max(255, "Cannot be longer than 255 characters").test("restricted-char-validation", "Characters : and | cannot be used", (val)=>!containsRestrictedPathChars(val || "")).required("Please enter a board name")
});
const pageIsValid = (errors, param)=>{
    let { title } = param;
    return !!title && !errors.title;
};
export const CreateBoardModal = (param)=>{
    let { title, trackLocation, onClose, selectedParentBoard, initialLibrary } = param;
    const { goToBoardPage } = useGoToBoardPage();
    const { createNewBoard } = useCreateNewBoard();
    const { showToast } = useToasts();
    const clipIdsToBeAdded = useSelector(selectedClipIdsSelector);
    const currentBoard = useSelector(centralizedBoardSelector);
    const [isLoading, setLoading] = useState(false);
    const { showingSubscriptionExpiredModal } = useShowSubscriptionExpiredModal({
        onClose
    });
    const { selectedBoard: parentBoard, isBoardSelected, isLibrarySelected, onSelectBoard, onSelectLibrary, selectedLibrary, shouldShowSubBoards, shouldShowLibraryBoards } = useSingleBoardSearchState({
        initialBoard: !initialLibrary ? selectedParentBoard || currentBoard : undefined,
        initialLibrary
    });
    const onBoardSelectChange = useCallback((param)=>{
        let { board, selected } = param;
        onSelectBoard(selected ? board : null);
    }, [
        onSelectBoard
    ]);
    const { getBoardPermissions } = useBoardPermissionsCache();
    const { getLibraryPermissions } = useLibraryPermissionsCache();
    const getIsBoardDisabled = useCallback((board)=>{
        const boardPermissions = getBoardPermissions(board.id);
        if (!canCreateBoard(boardPermissions)) {
            return {
                isDisabled: true,
                message: "You do not have permission create boards in this board"
            };
        }
    }, [
        getBoardPermissions
    ]);
    const getIsLibraryDisabled = useCallback((library)=>{
        const libraryPermissions = getLibraryPermissions(library.id);
        if (!canCreateBoard(libraryPermissions)) {
            return {
                isDisabled: true,
                message: "You do not have permission create boards in this library"
            };
        }
    }, [
        getLibraryPermissions
    ]);
    if (showingSubscriptionExpiredModal) {
        return null;
    }
    const onCreate = async (param)=>{
        let { title, parentId, libraryId } = param;
        setLoading(true);
        try {
            const board = await createNewBoard({
                board: {
                    title,
                    description: "",
                    parentId: parentId !== "root" ? parentId : undefined,
                    libraryId
                },
                clipIds: clipIdsToBeAdded,
                trackLocation,
                shouldFollow: true
            });
            if (!board) return;
            showToast(/*#__PURE__*/ _jsxs("div", {
                className: "flex w-full items-center justify-center overflow-hidden",
                children: [
                    "Created board ",
                    /*#__PURE__*/ _jsx(ToastLink, {
                        "data-testid": CREATED_BOARD_LINK,
                        className: "flex-1 pl-1.5",
                        onClick: ()=>goToBoardPage({
                                board,
                                trackLocation: "created-board-toast"
                            }),
                        children: title
                    })
                ]
            }));
            onClose();
        } catch (_error) {
            showToast("An error occurred - please try again later");
        } finally{
            setLoading(false);
        }
    };
    return /*#__PURE__*/ _jsx(Formik, {
        validationSchema: CreateBoardSchema,
        onSubmit: (param)=>{
            let { title } = param;
            var _parentBoard_library;
            onCreate({
                title: title.trim(),
                parentId: parentBoard === null || parentBoard === void 0 ? void 0 : parentBoard.id,
                libraryId: (selectedLibrary === null || selectedLibrary === void 0 ? void 0 : selectedLibrary.id) || (parentBoard === null || parentBoard === void 0 ? void 0 : (_parentBoard_library = parentBoard.library) === null || _parentBoard_library === void 0 ? void 0 : _parentBoard_library.id)
            });
        },
        initialValues: {
            title: title || ""
        },
        children: (param)=>{
            let { values, errors } = param;
            return /*#__PURE__*/ _jsxs(Modal, {
                isOpen: true,
                onDismiss: onClose,
                "data-testid": "CREATE_BOARD_MODAL",
                children: [
                    /*#__PURE__*/ _jsxs("header", {
                        className: "mb-4 flex justify-between gap-4",
                        children: [
                            /*#__PURE__*/ _jsx(ModalTitle, {
                                children: "Create a board"
                            }),
                            /*#__PURE__*/ _jsx(ModalCloseButton, {
                                className: "shrink-0",
                                onClick: onClose
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Form, {
                        id: "create-board-form",
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                className: "mb-4 inline-block text-grey-10",
                                children: "Boards keep groups of files organized in one place."
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "mb-8",
                                children: /*#__PURE__*/ _jsx(FormikField, {
                                    label: "Name",
                                    name: "title",
                                    id: "title",
                                    component: /*#__PURE__*/ _jsx(Input, {
                                        size: "extra-large",
                                        autoFocus: true,
                                        autoComplete: "off"
                                    })
                                })
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "mb-6",
                                children: [
                                    /*#__PURE__*/ _jsx("label", {
                                        className: "mb-1.5 block text-14 font-semibold text-grey-11",
                                        children: "Location"
                                    }),
                                    /*#__PURE__*/ _jsx(PrivateBoardSearch, {
                                        isBoardSelected: isBoardSelected,
                                        onBoardSelectChange: onBoardSelectChange,
                                        shouldShowSubBoards: shouldShowSubBoards,
                                        shouldShowLibraryBoards: shouldShowLibraryBoards,
                                        initialBoardId: parentBoard === null || parentBoard === void 0 ? void 0 : parentBoard.id,
                                        className: "[&>.workspace-boards]:h-[200px]",
                                        onSelectLibrary: onSelectLibrary,
                                        isLibrarySelected: isLibrarySelected,
                                        getIsBoardDisabled: getIsBoardDisabled,
                                        getIsLibraryDisabled: getIsLibraryDisabled
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("footer", {
                                className: "flex justify-end gap-3",
                                children: [
                                    /*#__PURE__*/ _jsx(Button, {
                                        appearance: "ghost",
                                        color: "grey",
                                        onClick: onClose,
                                        size: "large",
                                        children: "Cancel"
                                    }),
                                    /*#__PURE__*/ _jsx(Button, {
                                        "data-testid": "CREATE_BOARD_MODAL_SAVE",
                                        isLoading: isLoading,
                                        disabled: isLoading || !pageIsValid(errors, values),
                                        appearance: "filled",
                                        color: "blue",
                                        size: "large",
                                        type: "submit",
                                        children: "Save"
                                    })
                                ]
                            })
                        ]
                    })
                ]
            });
        }
    });
};
